import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SetupResponse } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { TODAY } from "./Today.js";
import { FirstDraft } from "./setup/FirstDraft.js";
import { Flag, flagOf, SETUP, STATE_TONE, stateOf, useSetup } from "./groups.js";

/**
 * **Customers → Groups** (38, ADR 0068): every group, for a business that will have dozens.
 *
 * One row each, saying what a person scans for: where it is filed, whether it is running, how far its companies have
 * got, and what it has cost this month. A row opens the group. Ticking rows pauses, starts or files several at once,
 * because a business with dozens of groups stops a region, not a group.
 *
 * A folder is only a label the owner types — a region, a product, a season. Nothing reads it but this list.
 */

type Filter = { kind: "all" } | { kind: "folder"; name: string } | { kind: "paused" } | { kind: "retired" };

export function GroupList({ onOpen, onNew }: { onOpen: (id: string) => void; onNew: () => void }) {
  const qc = useQueryClient();
  const q = useSetup();
  const today = useQuery({ queryKey: TODAY, queryFn: () => api.today() });
  const keep = (d: SetupResponse) => qc.setQueryData(SETUP, d);
  const draft = useMutation({ mutationFn: () => api.draftCustomersSetup(), onSuccess: keep });
  const mark = useMutation({
    mutationFn: (v: { ids: string[]; folder?: string; starred?: boolean; paused?: boolean }) => api.markGroups(v),
    onSuccess: (d) => { keep(d); setTicked(new Set()); },
  });
  const [filter, setFilter] = useState<Filter>({ kind: "all" });
  const [search, setSearch] = useState("");
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [folder, setFolder] = useState("");

  const d = q.data;
  if (q.isPending) return <p className="text-sm text-faint">…</p>;
  if (!d) return <p className="text-sm text-bad">{String(q.error ?? "That did not load.")}</p>;

  const pauses = today.data?.pauses ?? [];
  const live = d.groups.filter((g) => g.state !== "dropped");
  const folders = [...new Set(live.map((g) => g.folder).filter(Boolean))].sort();
  const words = search.trim().toLowerCase();
  const shown = d.groups
    .filter((g) => filter.kind === "retired" ? g.state === "dropped" : g.state !== "dropped")
    .filter((g) => filter.kind !== "folder" || g.folder === filter.name)
    .filter((g) => filter.kind !== "paused" || g.paused)
    .filter((g) => !words || g.name.toLowerCase().includes(words) || g.looksFor.toLowerCase().includes(words));
  const running = live.filter((g) => !g.paused).length;
  const tick = (id: string) => setTicked((now) => { const n = new Set(now); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const ids = [...ticked];

  const chip = (on: boolean, label: string, f: Filter) => (
    <button key={label} onClick={() => { setFilter(f); setTicked(new Set()); }}
      className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${on ? "bg-ink text-white border-ink" : "bg-page border-line-2 text-ink-2"}`}
    >{label}</button>
  );

  return (
    <div className="flex flex-col gap-3 max-w-[1100px]">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <h1 className="text-base font-semibold">Groups</h1>
        <span className="text-[12px] text-muted">
          {live.length} {live.length === 1 ? "group" : "groups"} · {running} running · each has its own companies and its own letter
        </span>
        {d.mayEdit ? (
          <button onClick={onNew} className="ml-auto px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold flex items-center gap-1.5">
            <Icon name="plus" size={12} /> New group
          </button>
        ) : null}
      </div>

      {!d.started ? <FirstDraft draft={() => draft.mutate()} busy={draft.isPending} error={draft.error} mayEdit={d.mayEdit} priceUsd={d.draftUsd} /> : null}

      <div className="flex flex-wrap items-center gap-1.5">
        <input
          value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a group" aria-label="Find a group"
          className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-page text-[13px] w-[200px] mr-1"
        />
        {chip(filter.kind === "all", "All", { kind: "all" })}
        {folders.map((f) => chip(filter.kind === "folder" && filter.name === f, f, { kind: "folder", name: f }))}
        {live.some((g) => g.paused) ? chip(filter.kind === "paused", "Paused", { kind: "paused" }) : null}
        {d.groups.some((g) => g.state === "dropped") ? chip(filter.kind === "retired", "Retired", { kind: "retired" }) : null}
      </div>

      {ids.length && d.mayEdit ? (
        <div role="toolbar" aria-label="Ticked groups" className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-panel px-3 py-2 text-[12px]">
          <span className="font-semibold">{ids.length} ticked</span>
          <button onClick={() => mark.mutate({ ids, paused: true })} disabled={mark.isPending} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page font-medium">Pause</button>
          <button onClick={() => mark.mutate({ ids, paused: false })} disabled={mark.isPending} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page font-medium">Start again</button>
          <span className="flex items-center gap-1 ml-2">
            <input
              value={folder} onChange={(e) => setFolder(e.target.value.slice(0, 40))} list="group-folders"
              aria-label="Folder" placeholder="a folder, or empty for none"
              className="px-2 py-1 rounded-lg border border-line-2 bg-page text-[12px] w-[170px]"
            />
            <datalist id="group-folders">{folders.map((f) => <option key={f} value={f} />)}</datalist>
            <button onClick={() => { mark.mutate({ ids, folder }); setFolder(""); }} disabled={mark.isPending} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page font-medium">Move to the folder</button>
          </span>
          {mark.error ? <span className="text-bad">{String(mark.error)}</span> : null}
        </div>
      ) : null}

      {shown.length ? (
        <div className="rounded-xl border border-line bg-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-[.06em] text-muted text-left border-b border-line">
                {d.mayEdit ? <th className="pl-3 py-2 w-6" /> : null}
                <th className="px-3 py-2 font-semibold">Group</th>
                <th className="px-3 py-2 font-semibold">Folder</th>
                <th className="px-3 py-2 font-semibold">State</th>
                <th className="px-3 py-2 font-semibold text-right">Found · written · answered</th>
                <th className="px-3 py-2 font-semibold text-right">This month</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((g) => {
                const state = stateOf(g, pauses);
                return (
                  <tr key={g.id} onClick={() => onOpen(g.id)} className="border-b border-line last:border-0 cursor-pointer hover:bg-page">
                    {d.mayEdit ? (
                      <td className="pl-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Tick ${g.name}`} checked={ticked.has(g.id)} onChange={() => tick(g.id)} />
                      </td>
                    ) : null}
                    <td className="px-3 py-2">
                      <span className="flex items-center gap-2">
                        <Flag code={flagOf(g)} />
                        <span className="font-semibold">{g.name}</span>
                        <Star on={g.starred} name={g.name} disabled={!d.mayEdit || mark.isPending}
                          onClick={() => mark.mutate({ ids: [g.id], starred: !g.starred })} />
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted">{g.folder || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold ${STATE_TONE[state]}`}>{state}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{g.found} · {g.written} · {g.answered}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{g.state === "dropped" && g.spentThisMonth === "0.00" ? "—" : `$${g.spentThisMonth}`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[12.5px] text-faint">{d.groups.length ? "Nothing matches." : "No groups yet. New group asks three questions and makes one."}</p>
      )}
      <p className="text-[11.5px] text-faint leading-relaxed">
        A folder is only a label you choose — a region, a product, a season. Starred groups sit in the menu.
        This month counts reading and writing to a group's companies; a search is counted only in the total on Today.
      </p>
    </div>
  );
}

/** A group's star: it sits in the menu when it is on. A press on it is not a press on the row. */
export function Star({ on, name, disabled, onClick }: { on: boolean; name: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      aria-label={on ? `Unstar ${name}` : `Star ${name}`} aria-pressed={on} disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`${on ? "text-coral" : "text-faint hover:text-ink-2"} disabled:opacity-60`}
    >
      <svg width={14} height={14} viewBox="0 0 24 24" fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2} strokeLinejoin="round" aria-hidden>
        <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2l1.1-6.2L3 9.6l6.2-.9z" />
      </svg>
    </button>
  );
}
